/**
 * First-touch attribution: capture `utm_*`, `gclid`, `fbclid`, the external
 * referrer, and the landing path ONCE on the first page load, persist to
 * localStorage, and never overwrite (first touch wins). Mirrors the write-once
 * localStorage pattern in `src/lib/api/token-storage.ts` (pending referral code).
 *
 * `parseAttribution` is a pure function (no `window`) so it is unit-testable in
 * the node-env Vitest setup; `captureFirstTouch` / `getAttribution` are the thin
 * SSR-safe wrappers a sync component calls. Everything no-ops when `window` is
 * undefined.
 */
import type { AttributionData } from "./types";

const STORAGE_KEY = "tenbagger.attribution";

/**
 * Parse an attribution snapshot from a URL + referrer. Pure — pass `now` in
 * tests for a deterministic `captured_at`.
 */
export function parseAttribution(
	url: string,
	referrer: string,
	ownHost: string,
	now: number = Date.now(),
): AttributionData {
	const parsed = new URL(url);
	const q = parsed.searchParams;

	let externalReferrer: string | null = null;
	if (referrer) {
		try {
			const refHost = new URL(referrer).host;
			if (refHost && refHost !== ownHost) externalReferrer = referrer;
		} catch {
			// malformed referrer → treat as none
		}
	}

	return {
		utm_source: q.get("utm_source"),
		utm_medium: q.get("utm_medium"),
		utm_campaign: q.get("utm_campaign"),
		utm_term: q.get("utm_term"),
		utm_content: q.get("utm_content"),
		gclid: q.get("gclid"),
		fbclid: q.get("fbclid"),
		referrer: externalReferrer,
		landing_path: parsed.pathname + parsed.search,
		captured_at: new Date(now).toISOString(),
	};
}

/** Read the stored snapshot, or `null` if never captured / unparseable. */
export function getAttribution(): AttributionData | null {
	if (typeof window === "undefined") return null;
	const raw = window.localStorage.getItem(STORAGE_KEY);
	if (!raw) return null;
	try {
		return JSON.parse(raw) as AttributionData;
	} catch {
		return null;
	}
}

/**
 * Capture first-touch attribution exactly once. If a snapshot already exists this
 * is a no-op (first touch wins). Returns the effective snapshot. Call from a
 * root-mounted sync component on first load.
 */
export function captureFirstTouch(): AttributionData | null {
	if (typeof window === "undefined") return null;

	const existing = getAttribution();
	if (existing) return existing;

	const snapshot = parseAttribution(
		window.location.href,
		document.referrer,
		window.location.host,
	);
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
	} catch {
		// private mode / quota — attribution is best-effort
	}
	return snapshot;
}
